import {
  NSSampleSchema,
  SchemaSearchForm,
  applyClientLookupsToSchema,
  deriveAccordionFieldMapFromSchema,
  expandAllAccordions,
  expandAccordionsByErrorPaths,
  sampleClientLookups,
  scrollToFirstError,
  useLookupOptions,
  useRawLookupOptions,
  validateAndSubmitSearchForm,
} from '@frontend/pricing-search';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  calculateGovie,
  getStateCounties,
  getZipLocations,
} from '../../../common/services/searchFormServices';
import { useIsZeroBasedPricing } from '../../../common/utils/customHooks/useIsZeroBasedPricing';
import { usePricingSearchConfiguration } from '../../../data/useSelectors';
import { PATH_DEBUG_RESULTS } from '../../../route/constants';
import { AppDispatch } from '../../../store';
import { searchFormActions } from '../../../store/searchForm';
import { setLoanInfo } from '../../components/Form/utils/common';
import { useExpandAccordionsOnError } from '../../components/Form/utils/customHooks';
import { ErrorBanner } from '../../SearchForm/components/ErrorBanner';
import ScenarioFooter from '../../Scenario/components/ScenarioFooter';
import { ScenarioPageContent } from '../../Scenario/components/styled';
import { exitToLegacyDebug } from '../utils';

export const DebugSearch: React.FC<{ schemaForm: any }> = ({ schemaForm }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isZeroBasedPricing = useIsZeroBasedPricing();
  const loanTermsLookups = useLookupOptions('loanTerms');
  const schema = usePricingSearchConfiguration();

  const [activeAccordions, setActiveAccordions] = useState<
    Record<number, boolean>
  >({});
  const [validationMessage, setValidationMessage] = useState<string>();

  const allLookups = useRawLookupOptions();
  const schemaWithLookups = useMemo(() => {
    if (!schema) return NSSampleSchema;
    return applyClientLookupsToSchema(
      schema,
      allLookups || sampleClientLookups,
    );
  }, [schema, allLookups]);

  const accordionFieldMap = useMemo(
    () => deriveAccordionFieldMapFromSchema(schemaWithLookups),
    [schemaWithLookups],
  );

  const { handleSearchFormInitialized } = useExpandAccordionsOnError({
    formReady: schemaForm,
    schemaWithLookups,
    schemaForm,
    accordionFieldMap,
    setActiveAccordions,
    loanTermsLookups,
  });

  const handleQualify = async () => {
    const result = await validateAndSubmitSearchForm({
      schema: schemaWithLookups,
      schemaForm,
      preview: false,
      loanTerms: loanTermsLookups,
    });

    if (result.valid) {
      setValidationMessage(undefined);
      dispatch(setLoanInfo(result.formData) as any);
      dispatch(searchFormActions.setBannerIsOpen(false));
      navigate(PATH_DEBUG_RESULTS);
    } else {
      dispatch(searchFormActions.setBannerIsOpen(true));
      if (result.errorPaths?.length) {
        const toOpen = expandAccordionsByErrorPaths(
          accordionFieldMap,
          result.errorPaths,
        );
        const accordionsToOpen = Object.keys(toOpen).length
          ? toOpen
          : expandAllAccordions(accordionFieldMap);
        setActiveAccordions((prev) => ({ ...prev, ...accordionsToOpen }));
        const firstError = schemaForm.getFieldState(result.errorPaths[0]).error;
        setValidationMessage(
          firstError?.message || 'Please correct the highlighted field.',
        );
        scrollToFirstError(result.errorPaths);
      } else {
        setValidationMessage(
          'The search form could not be validated. Please review the entered values.',
        );
      }
    }
  };

  return (
    <>
      <ErrorBanner body={validationMessage} />
      <ScenarioPageContent>
        {schemaForm && (
          <SchemaSearchForm
            schema={schemaWithLookups}
            schemaForm={schemaForm}
            preview={false}
            getStateCounties={getStateCounties}
            getZipLocations={getZipLocations}
            calculateGovie={calculateGovie}
            activeAccordions={activeAccordions}
            setActiveAccordions={setActiveAccordions}
            isZeroBasedPricing={isZeroBasedPricing}
            onInitialized={handleSearchFormInitialized}
          />
        )}
      </ScenarioPageContent>
      <ScenarioFooter
        onCancel={exitToLegacyDebug}
        onNext={handleQualify}
        nextLabel="Qualify"
      />
    </>
  );
};

export default DebugSearch;
